import moment from 'moment'
import {
  decryptSecure,
  encryptSecureString,
  isSecureEnabled,
} from 'Services/secureTransport'
const capitalizeFirstLetter = (str: string) => {
  // converting first letter to uppercase
  try {
    str = str?.toLowerCase()
    const capitalized = str.replace(/^./, str[0]?.toUpperCase())
    return capitalized
  } catch (e) {
    console.log(e)
    return ''
  }
}

async function base64UrlDecode(base64Url: string) {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const paddedBase64 = base64 + '==='.slice(0, (4 - (base64.length % 4)) % 4)
  const binaryString = atob(paddedBase64)
  const byteArray = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    byteArray[i] = binaryString.charCodeAt(i)
  }
  return byteArray
}

async function base64UrlEncode(arrayBuffer: ArrayBuffer) {
  const uint8Array = new Uint8Array(arrayBuffer)
  const byteArray = Array.from(uint8Array)
  const base64String = btoa(String.fromCharCode.apply(null, byteArray))
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  return base64String.replace(/\+/g, '-').replace(/\//g, '_')
}

export const extractVariables = (inputString: string) => {
  const operators = /[+\-*/]/
  const variables = inputString?.split(operators)?.filter((token) => token.trim() !== '')
  return variables
}

export const calculateDateExpression = (calculationString: string, variables: any) => {
  // Split the calculationString into individual tokens
  const tokens = calculationString.match(/[\w\s]+|[+*/]/g) ?? []
  const operators = ['+', '-']
  // Initialize a result variable to store the final result
  let result = ''
  let dateValue: any = ''
  let currentOperator = ''

  // Loop through the tokens and perform the calculations
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i].trim()
    if (token === 'currentdate') {
      dateValue = moment()
    }
    if (operators.includes(token)) {
      currentOperator = token
    }
    if (token.includes('year')) {
      const year = token.split(' ')?.[0]

      if (currentOperator === '+') {
        dateValue = moment(dateValue).add(year, 'years')
      }
      if (currentOperator === '-') {
        dateValue = moment(dateValue).subtract(year, 'years')
      }
      currentOperator = ''
    }
    if (token.includes('month')) {
      const month = token.split(' ')?.[0]

      if (currentOperator === '+') {
        dateValue = moment(dateValue).add(month, 'months')
      }
      if (currentOperator === '-') {
        dateValue = moment(dateValue).subtract(month, 'months')
      }
      currentOperator = ''
    }
    if (token.includes('day')) {
      const days = token.split(' ')?.[0]
      if (currentOperator === '+') {
        dateValue = moment(dateValue).add(days, 'days')
      }
      if (currentOperator === '-') {
        dateValue = moment(dateValue).subtract(days, 'days')
      }
      currentOperator = ''
    }
    if (token in variables) {
      if (currentOperator === '+') {
        dateValue = moment(dateValue).add(variables[token], 'years')
      }
      if (currentOperator === '-') {
        dateValue = moment(dateValue).subtract(variables[token], 'years')
      }
      currentOperator = ''
    }
  }
  try {
    result = moment(dateValue).format('yyyy-MM-DD')
  } catch (error) {
    return 'Error: Invalid expression'
  }
  return result
}

export const calculateExpression = (calculationString: string, variables: any) => {
  // Split the calculationString into individual tokens
  const tokens = calculationString.match(/[\w\s]+|[+\-*/]/g) ?? []

  // Initialize a result variable to store the final result
  let result = 0

  // Loop through the tokens and perform the calculations
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i].trim()
    if (token in variables) {
      // If the token is a variable, replace it with its value

      tokens[i] = variables[token]
    }
  }
  // Join the tokens back into a string and evaluate the expression
  const evaluatedExpression = tokens.join(' ')
  try {
    result = eval(evaluatedExpression)
  } catch (error) {
    return 'Error: Invalid expression'
  }
  return result
}

const formatNumberToINR = (ber: any) => {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(ber.ber)
}

/**
 * Envelope encryption. Uses the session-key scheme when enabled (dev only, never
 * PROD); otherwise the legacy static key.
 *
 * Every caller encrypts a WHOLE payload for an endpoint listed in SECURE_URLS, so
 * baseQuery attaches the x-session-key header for all of them.
 *
 * For encrypting a single FIELD inside an otherwise-plain body (e.g. `otp`), use
 * encryptMessageStatic — the server decrypts those in a separate code path that
 * does not go through the session-key middleware.
 */
async function encryptMessage(plainText: string, aesKey: string, iv: string) {
  if (isSecureEnabled()) {
    try {
      const encryptedData = await encryptSecureString(plainText)
      // Encrypted traffic is unreadable in the Network tab — log the plaintext here.
      let parsed: unknown = plainText
      try {
        parsed = JSON.parse(plainText)
      } catch {
        /* not JSON (e.g. a bare OTP) — log as-is */
      }
      console.log('[api ▶ payload]', parsed)
      return { status: 200, message: 'Success', encryptedData }
    } catch (error) {
      console.error('[api ▶ payload] encryption failed', error)
      return { status: 500, message: 'Error', error: error }
    }
  }

  // Legacy static-key path removed (VAPT #2) — see encryptMessageStatic below.
  throw new Error('[encryptMessage] session-key encryption unavailable')
}

/**
 * Legacy static-key AES-GCM. Always static, regardless of the session-key rollout.
 *
 * Used for FIELD-level encryption inside a plain body — the OTP on
 * /product/life/verify-otp and /product/life/declaration-verify-otp. Those endpoints
 * send no x-session-key header and the server decrypts that field with the static
 * key, so switching them would break OTP verification.
 *
 * Migrate only once backend confirms field-level decryption supports session keys.
 */
/* ---------------- DISABLED (VAPT #2): legacy static-key AES-GCM ----------------
async function encryptMessageStatic(plainText: string, aesKey: string, iv: string) {
  try {
    const aesKeyArray = new TextEncoder().encode(aesKey)
    const ivArray = new TextEncoder().encode(iv)

    // Import the AES key
    const importedKey = await window.crypto.subtle.importKey(
      'raw',
      aesKeyArray,
      { name: 'AES-GCM' },
      false,
      ['encrypt'],
    )

    // Perform AES-GCM encryption
    const encryptedArrayBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: ivArray,
      },
      importedKey,
      new TextEncoder().encode(plainText),
    )

    // Convert the encrypted ArrayBuffer to base64
    // const encryptedData = await base64UrlEncode(encryptedArrayBuffer);
    const encryptedData = () => {
      const byteArray = new Uint8Array(encryptedArrayBuffer)
      const byteString = byteArray.reduce((acc, byte) => acc + String.fromCharCode(byte), '')
      return window.btoa(byteString)
    }

    return { status: 200, message: 'Success', encryptedData: encryptedData() }
  } catch (error) {
    return { status: 500, message: 'Error', error: error }
  }
}
------------------------------------------------------------------------------ */

async function decryptMessage(encryptedData: string, aesKey: string, iv: string) {
  if (isSecureEnabled()) {
    try {
      const decryptedData = await decryptSecure(encryptedData)
      console.log('[api ◀ payload]', decryptedData)
      return { status: 200, message: 'Success', decryptedData }
    } catch (error) {
      console.error('[api ◀ payload] decryption failed', error)
      return { status: 500, message: 'Error', error: error as Error }
    }
  }

  // Legacy static-key path removed (VAPT #2).
  throw new Error('[decryptMessage] session-key decryption unavailable')

  /* -------------- DISABLED (VAPT #2): legacy static-key AES-GCM --------------
  try {
    const aesKeyArray = new TextEncoder().encode(aesKey)
    const ivArray = new TextEncoder().encode(iv)

    // Import the AES key
    const importedKey = await window.crypto.subtle.importKey(
      'raw',
      aesKeyArray,
      { name: 'AES-GCM' },
      false,
      ['decrypt'],
    )

    // Decode base64 and convert to Uint8Array
    const encryptedArrayBuffer = await base64UrlDecode(encryptedData)

    // Perform AES-GCM decryption
    const decryptedArrayBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivArray,
      },
      importedKey,
      encryptedArrayBuffer,
    )

    // Convert the decrypted ArrayBuffer to string
    const decryptedMessage = new TextDecoder().decode(decryptedArrayBuffer)

    // Try to parse the decrypted message as JSON
    try {
      return {
        status: 200,
        message: 'Success',
        decryptedData: JSON.parse(decryptedMessage),
      }
    } catch (error) {
      return {
        status: 200,
        message: 'Success',
        decryptedData: decryptedMessage,
      }
    }
  } catch (error) {
    return { status: 500, message: 'Error', error: error as Error }
  }
  -------------------------------------------------------------------------- */
}

function feetToMeters(feet: string | number | boolean): number {
  try {
    const meters = Number(feet) * 0.092903
    return meters
  } catch (error) {
    // return 0 when not able to parse the passed feet
    return 0
  }
}

// Function to convert meters to feet
function metersToFeet(meters: number): number {
  const feet = meters / 0.3048
  return feet
}

export {
  capitalizeFirstLetter,
  encryptMessage,
  decryptMessage,
  formatNumberToINR,
  feetToMeters,
  metersToFeet,
}
